import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../services/api";

// Async Thunks for Payment CRUD operations

// Get all payments
export const getAllPayments = createAsyncThunk(
  "payment/getAllPayments",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/payments");
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch payments"
      );
    }
  }
);

// Get my payments (created by logged-in user)
export const getMyPayments = createAsyncThunk(
  "payment/getMyPayments",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/payments/my");
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch your payments"
      );
    }
  }
);

// Get payments by party
export const getPaymentsByParty = createAsyncThunk(
  "payment/getPaymentsByParty",
  async (partyId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/payments/party/${partyId}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch payments"
      );
    }
  }
);

// Get all parties (for dropdown)
export const getAllParties = createAsyncThunk(
  "payment/getAllParties",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/parties");
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch parties"
      );
    }
  }
);

// Get single payment by ID
export const getPaymentById = createAsyncThunk(
  "payment/getPaymentById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/payment/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch payment"
      );
    }
  }
);

// Create new payment
export const createPayment = createAsyncThunk(
  "payment/createPayment",
  async (paymentData, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/payment", paymentData);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.errors?.join(", ") || 
        error.message || 
        "Failed to create payment"
      );
    }
  }
);

// Update payment
export const updatePayment = createAsyncThunk(
  "payment/updatePayment",
  async ({ id, paymentData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/payment/${id}`, paymentData);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.errors?.join(", ") || 
        error.message || 
        "Failed to update payment"
      );
    }
  }
);

// Delete payment
export const deletePayment = createAsyncThunk(
  "payment/deletePayment",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/payment/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to delete payment"
      );
    }
  }
);

// Search payments
export const searchPayments = createAsyncThunk(
  "payment/searchPayments",
  async (searchQuery, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/payments/search?search=${searchQuery}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to search payments"
      );
    }
  }
);

// Get payments with pagination
export const getPaymentsWithPagination = createAsyncThunk(
  "payment/getPaymentsWithPagination",
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/payments/paginated?page=${page}&limit=${limit}`);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch payments"
      );
    }
  }
);

// Payment Slice
const paymentSlice = createSlice({
  name: "payment",
  initialState: {
    payments: [],
    parties: [],
    currentPayment: null,
    loading: false,
    error: null,
    success: false,
    message: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  },
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    resetSuccess: (state) => {
      state.success = false;
    },
    setCurrentPayment: (state, action) => {
      state.currentPayment = action.payload;
    },
    clearCurrentPayment: (state) => {
      state.currentPayment = null;
    },
  },
  extraReducers: (builder) => {
    // Get all payments
    builder.addCase(getAllPayments.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAllPayments.fulfilled, (state, action) => {
      state.loading = false;
      state.payments = action.payload;
      state.error = null;
    });
    builder.addCase(getAllPayments.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get my payments
    builder.addCase(getMyPayments.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getMyPayments.fulfilled, (state, action) => {
      state.loading = false;
      state.payments = action.payload;
      state.error = null;
    });
    builder.addCase(getMyPayments.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get payments by party
    builder.addCase(getPaymentsByParty.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getPaymentsByParty.fulfilled, (state, action) => {
      state.loading = false;
      state.payments = action.payload;
      state.error = null;
    });
    builder.addCase(getPaymentsByParty.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get all parties
    builder.addCase(getAllParties.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAllParties.fulfilled, (state, action) => {
      state.loading = false;
      state.parties = action.payload;
      state.error = null;
    });
    builder.addCase(getAllParties.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get payment by ID
    builder.addCase(getPaymentById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getPaymentById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentPayment = action.payload;
      state.error = null;
    });
    builder.addCase(getPaymentById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Create payment
    builder.addCase(createPayment.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(createPayment.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.payments.unshift(action.payload);
      state.message = "Payment created successfully";
      state.error = null;
    });
    builder.addCase(createPayment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Update payment
    builder.addCase(updatePayment.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(updatePayment.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.message = "Payment updated successfully";
      state.payments = state.payments.map((payment) =>
        payment._id === action.payload._id ? action.payload : payment
      );
      if (state.currentPayment?._id === action.payload._id) {
        state.currentPayment = action.payload;
      }
      state.error = null;
    });
    builder.addCase(updatePayment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Delete payment
    builder.addCase(deletePayment.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(deletePayment.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.message = "Payment deleted successfully";
      state.payments = state.payments.filter(
        (payment) => payment._id !== action.payload
      );
      if (state.currentPayment?._id === action.payload) {
        state.currentPayment = null;
      }
      state.error = null;
    });
    builder.addCase(deletePayment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Search payments
    builder.addCase(searchPayments.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(searchPayments.fulfilled, (state, action) => {
      state.loading = false;
      state.payments = action.payload;
      state.error = null;
    });
    builder.addCase(searchPayments.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get payments with pagination
    builder.addCase(getPaymentsWithPagination.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getPaymentsWithPagination.fulfilled, (state, action) => {
      state.loading = false;
      state.payments = action.payload.data;
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit || 10,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
      };
      state.error = null;
    });
    builder.addCase(getPaymentsWithPagination.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  },
});

export const {
  clearErrors,
  clearMessage,
  resetSuccess,
  setCurrentPayment,
  clearCurrentPayment,
} = paymentSlice.actions;

export default paymentSlice.reducer;

